import { PhoneCall, ShieldCheck, MessageCircle, Siren, FileLock2, MapPin, Activity, Power } from "lucide-react";

const FEATURES = [
  {
    icon: PhoneCall,
    title: "Masked calls, always.",
    body: "Every call is routed through a one-time bridge. Your real number never leaves our servers.",
    span: "md:col-span-7",
    tone: "dark",
  },
  {
    icon: Siren,
    title: "Emergency channel",
    body: "A red Emergency button on your scan page reaches your nominated contacts instantly.",
    span: "md:col-span-5",
    tone: "yellow",
  },
  {
    icon: MessageCircle,
    title: "SMS, WhatsApp, voice — pick what reaches you.",
    body: "Choose any combination per tag. Set quiet hours. Block a number from your app in one tap.",
    span: "md:col-span-5",
  },
  {
    icon: FileLock2,
    title: "Documents on the tag",
    body: "Store your registration, insurance, inspection. Owner-only access with OTP.",
    span: "md:col-span-7",
  },
  {
    icon: MapPin,
    title: "Scan map",
    body: "See where, when and how often your tag is scanned — straight from the app.",
    span: "md:col-span-4",
  },
  {
    icon: Activity,
    title: "Vehicle in/out logs",
    body: "For societies, dealerships and fleets — automatic entry/exit timestamps via QR.",
    span: "md:col-span-4",
  },
  {
    icon: Power,
    title: "Pause anytime",
    body: "Disable calls, transfer the tag, or freeze the QR with one tap.",
    span: "md:col-span-4",
  },
];

export default function FeatureGrid() {
  return (
    <section id="features" data-testid="features-section" className="section-pad bg-white">
      <div className="container-secqre">
        <div className="max-w-3xl">
          <span className="overline" data-testid="features-overline">— Features</span>
          <h2 className="mt-4 font-heading font-bold text-4xl sm:text-5xl tracking-tight" data-testid="features-heading">
            One sticker. Everything a private driver needs.
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            SECQRE is engineered like infrastructure. Calls relay through us, your number stays
            on our servers, and you keep full control from a phone app that takes seconds to learn.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {FEATURES.map((f, i) => (
            <FeatureCard key={i} {...f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ icon: Icon, title, body, span, tone, index }) {
  const base = "rounded-2xl border p-7 md:p-9 transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(15,23,42,0.15)]";
  const styles = tone === "dark"
    ? "bg-[#0F172A] border-[#0F172A] text-white"
    : tone === "yellow"
    ? "bg-[#FFD600] border-[#FFD600] text-[#0A0A0A]"
    : "bg-white border-[#E2E8F0] text-[#0A0A0A]";
  return (
    <div className={`${base} ${styles} ${span} col-span-1`} data-testid={`feature-card-${index}`}>
      <div className={`w-11 h-11 rounded-lg inline-flex items-center justify-center mb-6 ${
        tone === "dark" ? "bg-white/10" : tone === "yellow" ? "bg-[#0A0A0A] text-white" : "bg-[#F8F9FA]"
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-heading text-2xl font-bold leading-tight">{title}</h3>
      <p className={`mt-3 leading-relaxed ${tone === "dark" ? "text-white/70" : tone === "yellow" ? "text-[#0A0A0A]/80" : "text-[#475569]"}`}>
        {body}
      </p>
    </div>
  );
}
